/**
 * Retry Logic Utility
 *
 * Implements exponential backoff retry for transient failures.
 * Includes comprehensive error handling for network and API errors.
 */

import { API_CONFIG } from '../utils/constants';
import { RETRYABLE_STATUS_CODES } from '../types/api.types';
import { logger } from '../utils/logger';

/**
 * Retry Configuration
 */
export interface RetryConfig {
  /**
   * Maximum number of retry attempts
   * Default: 3
   */
  maxAttempts: number;

  /**
   * Initial delay before first retry (milliseconds)
   * Default: 1000ms (1 second)
   */
  initialDelayMs: number;

  /**
   * Maximum delay between retries (milliseconds)
   * Default: 10000ms (10 seconds)
   */
  maxDelayMs: number;

  /**
   * Multiplier for exponential backoff
   * Default: 2 (doubles each retry)
   */
  backoffMultiplier: number;

  /**
   * HTTP status codes that should trigger retry
   * Default: [408, 429, 500, 502, 503, 504]
   */
  retryableStatusCodes: readonly number[];
}

/**
 * Default retry configuration
 */
const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxAttempts: API_CONFIG.RETRY_ATTEMPTS,
  initialDelayMs: API_CONFIG.RETRY_DELAY_MS,
  maxDelayMs: API_CONFIG.RETRY_MAX_DELAY_MS,
  backoffMultiplier: API_CONFIG.RETRY_BACKOFF_MULTIPLIER,
  retryableStatusCodes: RETRYABLE_STATUS_CODES,
};

/**
 * Retry an async function with exponential backoff
 *
 * @param fn - Async function to retry
 * @param config - Retry configuration (optional)
 * @returns Promise that resolves with function result or rejects after max attempts
 *
 * @example
 * const result = await withRetry(
 *   () => fetch('/api/endpoint'),
 *   { maxAttempts: 3, initialDelayMs: 1000 }
 * );
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  config: Partial<RetryConfig> = {}
): Promise<T> {
  const finalConfig: RetryConfig = { ...DEFAULT_RETRY_CONFIG, ...config };

  let lastError: Error | unknown;
  let attempt = 0;

  while (attempt < finalConfig.maxAttempts) {
    try {
      return await fn();
    } catch (error) {
      attempt++;
      lastError = error;

      // Check if error is retryable
      if (!isRetryableError(error, finalConfig.retryableStatusCodes)) {
        logger.debug('Error is not retryable, aborting', { error, attempt });
        throw error;
      }

      // Check if max attempts reached
      if (attempt >= finalConfig.maxAttempts) {
        logger.debug('Max retry attempts reached', { attempt, maxAttempts: finalConfig.maxAttempts });
        throw error;
      }

      // Calculate delay with exponential backoff
      const delay = calculateBackoffDelay(attempt, finalConfig);

      logger.debug('Retrying after delay', {
        attempt,
        delayMs: delay,
        error: error instanceof Error ? error.message : 'Unknown error',
      });

      // Wait before retrying
      await sleep(delay);
    }
  }

  throw lastError;
}

/**
 * Check if error should trigger retry
 *
 * @param error - Error to check
 * @param retryableStatusCodes - HTTP status codes that should trigger retry
 * @returns true if error is retryable, false otherwise
 */
export function isRetryableError(error: unknown, retryableStatusCodes: readonly number[]): boolean {
  // Network errors (no response) are retryable
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return true;
  }

  // Check if error has statusCode property (API errors)
  if (error && typeof error === 'object' && 'statusCode' in error) {
    const statusCode = (error as { statusCode: number }).statusCode;
    return retryableStatusCodes.includes(statusCode);
  }

  // Response objects (for direct fetch usage)
  if (error && typeof error === 'object' && 'status' in error) {
    const status = (error as { status: number }).status;
    return retryableStatusCodes.includes(status);
  }

  // Default: don't retry unknown errors
  return false;
}

/**
 * Calculate delay for exponential backoff
 *
 * Formula: min(initialDelay * (backoffMultiplier ^ (attempt - 1)), maxDelay)
 *
 * @param attempt - Current attempt number (1-indexed)
 * @param config - Retry configuration
 * @returns Delay in milliseconds
 *
 * @example
 * calculateBackoffDelay(1, config) // 1000ms
 * calculateBackoffDelay(2, config) // 2000ms
 * calculateBackoffDelay(3, config) // 4000ms
 * calculateBackoffDelay(4, config) // 8000ms
 * calculateBackoffDelay(5, config) // 10000ms (capped at maxDelay)
 */
function calculateBackoffDelay(attempt: number, config: RetryConfig): number {
  const delay = config.initialDelayMs * Math.pow(config.backoffMultiplier, attempt - 1);
  return Math.min(delay, config.maxDelayMs);
}

/**
 * Sleep for specified duration
 *
 * @param ms - Duration in milliseconds
 * @returns Promise that resolves after duration
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Retry with custom predicate
 *
 * More flexible version that allows custom retry logic
 *
 * @param fn - Async function to retry
 * @param shouldRetry - Predicate function that determines if retry should happen
 * @param config - Retry configuration
 * @returns Promise that resolves with function result or rejects after max attempts
 *
 * @example
 * const result = await retryWithPredicate(
 *   () => apiCall(),
 *   (error) => error.code === 'RATE_LIMIT',
 *   { maxAttempts: 5 }
 * );
 */
export async function retryWithPredicate<T>(
  fn: () => Promise<T>,
  shouldRetry: (error: unknown, attempt: number) => boolean,
  config: Partial<RetryConfig> = {}
): Promise<T> {
  const finalConfig: RetryConfig = { ...DEFAULT_RETRY_CONFIG, ...config };

  let lastError: Error | unknown;
  let attempt = 0;

  while (attempt < finalConfig.maxAttempts) {
    try {
      return await fn();
    } catch (error) {
      attempt++;
      lastError = error;

      // Check custom predicate
      if (!shouldRetry(error, attempt)) {
        throw error;
      }

      // Check if max attempts reached
      if (attempt >= finalConfig.maxAttempts) {
        throw error;
      }

      // Calculate delay and retry
      const delay = calculateBackoffDelay(attempt, finalConfig);
      await sleep(delay);
    }
  }

  throw lastError;
}
